
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Bug, ExternalLink, Save, RefreshCw } from 'lucide-react';
import { useSiteConfig } from '@/hooks/useSiteConfig';
import { useSiteConfigValue } from '@/hooks/useSiteConfigValue';
import { useToast } from '@/hooks/use-toast';

const AdminSiteConfigBugReportForm = () => {
  const { toast } = useToast();
  const { updateConfiguration } = useSiteConfig();
  const { value: currentBugReportUrl, loading, refetch } = useSiteConfigValue('bug_report_form_url');
  const [saving, setSaving] = useState(false);
  const [tempUrl, setTempUrl] = useState('');

  React.useEffect(() => {
    if (currentBugReportUrl && !tempUrl) {
      setTempUrl(currentBugReportUrl);
      console.log('AdminSiteConfigBugReportForm - Set tempUrl to:', currentBugReportUrl);
    }
  }, [currentBugReportUrl, tempUrl]);

  const handleUpdate = async () => {
    if (!tempUrl.trim()) {
      toast({
        title: "Validation Error",
        description: "URL cannot be empty.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      console.log('AdminSiteConfigBugReportForm - Updating bug report URL from:', currentBugReportUrl, 'to:', tempUrl);
      
      const result = await updateConfiguration('bug_report_form_url', tempUrl.trim());
      console.log('AdminSiteConfigBugReportForm - Update result:', result);
      
      // Force a refetch after a short delay
      setTimeout(() => {
        console.log('AdminSiteConfigBugReportForm - Forcing refetch...');
        refetch();
      }, 1000);
      
      toast({
        title: "Bug Report URL Updated",
        description: "The bug report form URL has been updated successfully.",
      });
    } catch (error) {
      console.error('AdminSiteConfigBugReportForm - Error updating bug report URL:', error);
      toast({
        title: "Error",
        description: "Failed to update bug report URL. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const testUrl = () => {
    if (tempUrl) {
      console.log('AdminSiteConfigBugReportForm - Testing URL:', tempUrl);
      window.open(tempUrl, '_blank');
    }
  };

  const handleRefresh = () => {
    console.log('AdminSiteConfigBugReportForm - Manual refresh triggered');
    refetch();
  };

  if (loading) {
    return (
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center space-x-2">
            <Bug className="w-5 h-5" />
            <span>Bug Report Form Configuration</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-white/10 rounded w-1/3"></div>
            <div className="h-10 bg-white/10 rounded w-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  console.log('AdminSiteConfigBugReportForm - Rendering with:', {
    currentBugReportUrl,
    tempUrl,
    loading,
    saving
  });

  return (
    <Card className="bg-white/10 backdrop-blur-md border-white/20">
      <CardHeader>
        <CardTitle className="text-white flex items-center space-x-2">
          <Bug className="w-5 h-5" />
          <span>Bug Report Form Configuration</span>
          <Badge className="bg-blue-600/20 text-blue-300 border-blue-500/30">
            Help Center Integration
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="text-white">Bug Report Form URL</Label>
          <div className="flex space-x-2">
            <Input
              type="url"
              value={tempUrl}
              onChange={(e) => {
                console.log('AdminSiteConfigBugReportForm - URL changed to:', e.target.value);
                setTempUrl(e.target.value);
              }}
              disabled={saving}
              placeholder="https://forms.google.com/..."
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
            {tempUrl && (
              <Button
                variant="ghost"
                size="sm"
                onClick={testUrl}
                className="text-blue-400 hover:text-blue-300"
                disabled={saving}
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              className="text-yellow-400 hover:text-yellow-300"
              disabled={saving}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-gray-400">
            This URL will be used for the "Report Bug" button in the Help Center.
          </p>
        </div>

        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-400">
            Current: {currentBugReportUrl || 'Not set'}
          </div>
          <Button
            onClick={handleUpdate}
            disabled={saving || !tempUrl.trim() || tempUrl === currentBugReportUrl}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Updating...' : 'Update URL'}
          </Button>
        </div>

        {/* Debug Info */}
        <div className="text-xs text-gray-500 mt-4 p-2 bg-black/20 rounded">
          <div>Current URL: {currentBugReportUrl}</div>
          <div>Temp URL: {tempUrl}</div>
          <div>Loading: {loading.toString()}</div>
          <div>Saving: {saving.toString()}</div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminSiteConfigBugReportForm;
